import { useCallback } from 'react';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { RefetchOptions } from 'axios-hooks';
import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api/makeQuoteApiHttp';
import {
  GetDetailsResponseType,
  ProductType,
  transformAppDetails,
  transformCoverages,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  transformSaveCoveragesRequest,
  transformSaveCoveragesRequestViaApplication,
} from 'QuoteApp/services/endpoints/PostSaveCoverages';
import { getErrorMessage } from 'QuoteApp/utils';
import { useAgentUnauthorized } from 'QuoteApp/utils/hooks';
import { AgentUnauthorizedType } from 'QuoteApp/utils/hooks/useAgentUnauthorized';

export type SaveCoveragesResponseType =
  | GetDetailsResponseType
  | ReturnType<typeof transformCoverages>;

export type QuoteAppApiProviderType = {
  isUnauthorized: AgentUnauthorizedType['isUnauthorized'];
  handleUnauthorizedAgent: AgentUnauthorizedType['handleUnauthorizedAgent'];
  api: {
    saveAppData: QuoteAppApiRequestType<GetDetailsResponseType>;
    saveCoverages: QuoteAppApiRequestType<SaveCoveragesResponseType>;
    loading: boolean;
  };
};

type ErrorConfig = {
  suppressDefaultToastError?: boolean;
  throwException?: boolean;
};

type Meta = {
  updateViaApplication?: boolean;
  productType?: ProductType;
};

type QuoteAppApiRequestType<T> = (
  axiosConfig: AxiosRequestConfig,
  errorConfig?: ErrorConfig,
  meta?: Meta
) => AxiosPromise<T>;

type RequestFnType = (
  config?: AxiosRequestConfig | undefined,
  options?: RefetchOptions | undefined
) => AxiosPromise<any>;

export const useApi = (appId: string): QuoteAppApiProviderType => {
  const { isUnauthorized, handleUnauthorizedAgent } =
    useAgentUnauthorized(appId);
  const { toast } = useToast();

  /*
    Wrapper function that calls the supplied requestFn and handles errors consistently
  */
  const makeRequest = useCallback(
    async (
      requestFn: RequestFnType,
      axiosConfig: AxiosRequestConfig,
      errorConfig?: ErrorConfig
    ) => {
      try {
        return await requestFn(axiosConfig);
      } catch (e) {
        const unauthorized = handleUnauthorizedAgent(e);
        if (!unauthorized && !errorConfig?.suppressDefaultToastError) {
          toast({
            type: 'error',
            message: getErrorMessage(e),
            withSecondaryNav: false,
            icon: 'error',
          });
        }
        throw e;
      }
    },
    [handleUnauthorizedAgent, toast]
  );

  /*
    Each api request that needs to live at the top level of the app is instantiated here
    first with the useLazyQuoteApi hook. This prevents the requests from being canceled on
    navigation when the screen level component unmounts.

    To start, only the main "update" requests are being created here since they have caused
    the most issues. More can be added as needed if issues are identified with those requests.
  */
  const [{ loading: saveAppDataInProgress }, _saveAppData] =
    useLazyQuoteApi<GetDetailsResponseType>({
      method: 'POST',
      url: `/insurance_applications/${appId}/update`,
      transformResponse: transformAppDetails,
    });

  const [{ loading: saveCoveragesInProgress }, _saveCoverages] =
    useLazyQuoteApi<ReturnType<typeof transformCoverages>>({
      method: 'POST',
      url: `/insurance_applications/${appId}/insurance_products/personalAuto/update_coverages`,
      transformRequest: transformSaveCoveragesRequest,
      transformResponse: transformCoverages,
    });

  /*
    Each request created in the hooks above is then wrapped in a useCallback
    and passed into makeRequest to handle errors and pass the data or error
    through to the consuming component
  */
  const saveAppData = useCallback(
    async (axiosConfig: AxiosRequestConfig, errorConfig?: ErrorConfig) => {
      return makeRequest(_saveAppData, axiosConfig, errorConfig);
    },
    [_saveAppData, makeRequest]
  );

  const saveCoverages = useCallback(
    async (
      axiosConfig: AxiosRequestConfig,
      errorConfig?: ErrorConfig,
      meta?: Meta
    ) => {
      if (meta?.updateViaApplication) {
        return makeRequest(
          _saveAppData,
          {
            ...axiosConfig,
            transformRequest: (values) =>
              transformSaveCoveragesRequestViaApplication(
                values,
                meta?.productType!
              ),
          },
          errorConfig
        );
      } else {
        return makeRequest(_saveCoverages, axiosConfig, errorConfig);
      }
    },
    [_saveAppData, _saveCoverages, makeRequest]
  );

  return {
    isUnauthorized,
    handleUnauthorizedAgent,
    api: {
      saveAppData,
      saveCoverages,
      loading: saveAppDataInProgress || saveCoveragesInProgress,
    },
  };
};
